import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import SearchIcon from "pages/flyer/builder/drawer/components/Primitives/icons/SearchIcon";
import styles from "./KeyboardShortcutsModal.module.css";
import { isMacPlatform, Key, KeyCombo, OrCombo, SECTIONS, Section, Shortcut } from "./shortcuts";

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const ChevronIcon: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#2E3860"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ transition: "transform 150ms ease", transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2E3860" strokeWidth="2" strokeLinecap="round">
    <line x1="6" y1="6" x2="18" y2="18" />
    <line x1="18" y1="6" x2="6" y2="18" />
  </svg>
);

/** Single key chip — kind drives the width treatment per the design spec. */
const KeyBox: React.FC<{ k: Key }> = ({ k }) => (
  <div className={k.kind === "wide" ? styles.keyWide : styles.keySquare}>{k.label}</div>
);

/** Renders one combo as `Key + Key + Key` with `+` separators. */
const ComboRow: React.FC<{ combo: KeyCombo }> = ({ combo }) => (
  <>
    {combo.map((k, i) => (
      <React.Fragment key={i}>
        {i > 0 && <span className={styles.plus}>+</span>}
        <KeyBox k={k} />
      </React.Fragment>
    ))}
  </>
);

/** Right-side cell — either a single combo or "combo1 or combo2". */
const ShortcutKeys: React.FC<{ value: KeyCombo | OrCombo }> = ({ value }) => {
  if (Array.isArray(value)) {
    return (
      <div className={styles.keys}>
        <ComboRow combo={value} />
      </div>
    );
  }
  return (
    <div className={styles.keys}>
      <ComboRow combo={value.or[0]} />
      <span className={styles.orLabel}>or</span>
      <ComboRow combo={value.or[1]} />
    </div>
  );
};

const SectionItem: React.FC<{ item: Shortcut; mac: boolean }> = ({ item, mac }) => (
  <div className={styles.item}>
    <span className={styles.itemName}>{item.name}</span>
    <ShortcutKeys value={mac ? item.mac : item.win} />
  </div>
);

/** Chunk a flat item list into 2-column rows so the CSS grid + the
 *  full-width row separator render in the same order they appear on
 *  screen. The last (odd) item, if any, ends up alone in a row of 1. */
const pairItems = (items: Shortcut[]): Shortcut[][] => {
  const rows: Shortcut[][] = [];
  for (let i = 0; i < items.length; i += 2) {
    rows.push(items.slice(i, i + 2));
  }
  return rows;
};

const CollapsibleSection: React.FC<{
  section: Section;
  open: boolean;
  onToggle: () => void;
  mac: boolean;
}> = ({ section, open, onToggle, mac }) => {
  const rows = useMemo(() => pairItems(section.items), [section.items]);
  return (
    <div className={styles.section}>
      <button
        type="button"
        className={styles.sectionHeader}
        onClick={(e) => {
          // Belt-and-suspenders: stopPropagation here AND target-check
          // on the overlay. Without this an inert ancestor event handler
          // (e.g. a Menu portal that just unmounted) was still seeing
          // the click before the overlay's target-check could reject it.
          e.stopPropagation();
          onToggle();
        }}
        aria-expanded={open}
        data-cypress-id={`keyboard-shortcuts-section-toggle-${section.id}`}
      >
        <span className={styles.sectionTitle}>{section.title}</span>
        <ChevronIcon open={open} />
      </button>
      <div className={styles.sectionDivider} />
      {open && (
        <div className={styles.sectionBody}>
          {rows.map((row, rowIdx) => (
            <React.Fragment key={rowIdx}>
              <SectionItem item={row[0]} mac={mac} />
              {/* Only emit the vertical divider when this row actually has
                  a second item. Lonely items (odd count → last row) skip
                  both the divider and the second column entirely, so the
                  blue-light line never appears next to a solo shortcut. */}
              {row.length === 2 && <div className={styles.rowDivider} />}
              {row.length === 2 && <SectionItem item={row[1]} mac={mac} />}
              {rowIdx < rows.length - 1 && <div className={styles.rowSeparator} />}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export const KeyboardShortcutsModal: React.FC<Props> = ({ isOpen, onClose }) => {
  // Detect platform once on mount — the modal doesn't expect to migrate
  // between Mac and Windows mid-session.
  const mac = useMemo(() => isMacPlatform(), []);

  // Section open state. All sections default to open so users see every
  // shortcut on first glance; clicking the header collapses individual
  // sections. Multiple sections can be open at once (not accordion).
  const [openSections, setOpenSections] = useState<Record<Section["id"], boolean>>(() =>
    SECTIONS.reduce((acc, s) => ({ ...acc, [s.id]: true }), {} as Record<Section["id"], boolean>)
  );

  // Search query. When non-empty the modal switches to a flat-result
  // view: every item across all sections whose name contains the query
  // (case-insensitive) is rendered in the same 2-column grid as a
  // regular section body, without the section headers / chevrons. The
  // 6 collapse states above are unaffected — when the user clears the
  // input, the section view restores to whatever the user had opened.
  const [query, setQuery] = useState("");
  const trimmedQuery = query.trim().toLowerCase();
  const isSearching = trimmedQuery.length > 0;
  const searchResults = useMemo<Shortcut[]>(() => {
    if (!isSearching) return [];
    const results: Shortcut[] = [];
    SECTIONS.forEach((section) => {
      section.items.forEach((item) => {
        if (item.name.toLowerCase().includes(trimmedQuery)) {
          results.push(item);
        }
      });
    });
    return results;
  }, [isSearching, trimmedQuery]);

  // Close on Escape — standard modal behavior, matches the X button.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const toggle = (id: Section["id"]) => setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));

  // Portal directly to document.body so the modal lives outside the
  // Header's React subtree. Without this the synthetic-event bubble
  // path runs through the MUI Menu render boundary (the dropdown that
  // launched this modal) — any stray click listener installed by
  // MUI's Popover machinery or any other Header ancestor would
  // intercept our clicks and close the modal unexpectedly. Portaling
  // keeps the DOM placement at the body level while the React tree
  // bubbles up to its own root, isolating us.
  const modal = (
    <div
      className={styles.overlay}
      onClick={(e) => {
        // Backdrop click closes. Only when the click was directly on
        // the overlay surface, not a descendant.
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className={styles.card} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
        <div className={styles.inner}>
          {/* Close row sits ABOVE the title — anchored to the upper-right
              corner of the card so the X never visually competes with
              the centered title text. */}
          <div className={styles.closeRow}>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              aria-label="Close"
              data-cypress-id="keyboard-shortcuts-modal-close-button"
            >
              <CloseIcon />
            </button>
          </div>
          <div className={styles.titleRow}>
            <h2 className={styles.title}>Keyboard Shortcuts</h2>
          </div>
          {/* Search input. Filtering is `includes`-substring on
              `item.name`, case-insensitive; results bypass the
              section / chevron / divider chrome and render as a flat
              2-column list directly. */}
          <div className={styles.searchBar}>
            <span className={styles.searchIconWrap}>
              <SearchIcon />
            </span>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search shortcuts"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              aria-label="Search shortcuts"
              data-cypress-id="keyboard-shortcuts-modal-search-input"
            />
          </div>
          <div className={styles.scrollArea}>
            {isSearching ? (
              searchResults.length > 0 ? (
                // Reuse `.sectionBody` so the 2-column grid + vertical
                // divider + row-separator styles apply unchanged. The
                // collapse chrome is intentionally absent. Pairing
                // goes through the shared `pairItems` helper so a
                // future change to the row-chunking rule only needs
                // to land in one place.
                <div className={styles.sectionBody}>
                  {pairItems(searchResults).map((row, rowIdx, rows) => (
                    <React.Fragment key={rowIdx}>
                      <SectionItem item={row[0]} mac={mac} />
                      {row.length === 2 && <div className={styles.rowDivider} />}
                      {row.length === 2 && <SectionItem item={row[1]} mac={mac} />}
                      {rowIdx < rows.length - 1 && <div className={styles.rowSeparator} />}
                    </React.Fragment>
                  ))}
                </div>
              ) : (
                <div className={styles.noResults}>No shortcuts match &ldquo;{query}&rdquo;</div>
              )
            ) : (
              SECTIONS.map((section) => (
                <CollapsibleSection
                  key={section.id}
                  section={section}
                  open={openSections[section.id]}
                  onToggle={() => toggle(section.id)}
                  mac={mac}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default KeyboardShortcutsModal;
